import { useEffect, useState } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import { api, clearSession, getToken } from './api/client';
import SysAdminApp from './SysAdminApp';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ChildrenPage from './pages/ChildrenPage';
import ChoresPage from './pages/ChoresPage';
import DirectivesPage from './pages/DirectivesPage';
import ChallengesPage from './pages/ChallengesPage';
import ChatArchivePage from './pages/ChatArchivePage';
import MorePage from './pages/MorePage';
import SettingsPage from './pages/SettingsPage';
import { ensurePushSubscription, isPushRememberedReady, isPushSupported, syncExistingPushSubscription } from './lib/pushNotifications';
import type { Parent } from '@xiaopeiban/shared';

export default function App() {
  const [parent, setParent] = useState<Parent | null>(null);
  const [loading, setLoading] = useState(true);
  const [pushState, setPushState] = useState<'ready' | 'prompt' | 'busy' | 'denied' | 'unsupported' | 'not_configured'>(() => {
    if (typeof window === 'undefined') return 'unsupported';
    if (!isPushSupported()) return 'unsupported';
    if (isPushRememberedReady() || Notification.permission === 'granted') return 'ready';
    return 'prompt';
  });
  const location = useLocation();
  const isSysAdmin = location.pathname === '/sys' || location.pathname.startsWith('/sys/');

  useEffect(() => {
    if (isSysAdmin) { setLoading(false); return; }
    const token = getToken();
    if (!token) { setLoading(false); return; }
    api.getMe()
      .then(r => setParent(r.parent))
      .catch(() => { clearSession(); setParent(null); })
      .finally(() => setLoading(false));
  }, [isSysAdmin]);

  useEffect(() => {
    if (!parent || !isPushSupported() || Notification.permission !== 'granted') return;
    syncExistingPushSubscription()
      .then((ok) => setPushState(ok ? 'ready' : 'prompt'))
      .catch(() => setPushState('prompt'));
  }, [parent]);

  if (isSysAdmin) {
    return <SysAdminApp />;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (!parent) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage onLogin={setParent} />} />
        <Route path="*" element={<Navigate to="/login" replace state={{ from: location.pathname }} />} />
      </Routes>
    );
  }

  return (
    <>
      <nav className="bottom-nav nav-5">
        <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="bottom-nav-icon">📊</span>仪表盘
        </NavLink>
        <NavLink to="/chores" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="bottom-nav-icon">🧹</span>家务
        </NavLink>
        <NavLink to="/challenges" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="bottom-nav-icon">🎯</span>挑战
        </NavLink>
        <NavLink to="/ai" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="bottom-nav-icon">💬</span>AI嘱托
        </NavLink>
        <NavLink to="/more" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="bottom-nav-icon">⚙️</span>更多
        </NavLink>
      </nav>

      {pushState !== 'ready' && pushState !== 'unsupported' && (
        <button
          className={`admin-push-toggle ${pushState}`}
          disabled={pushState === 'busy'}
          onClick={async () => {
            setPushState('busy');
            try {
              const result = await ensurePushSubscription();
              setPushState(result === 'enabled' ? 'ready' : result);
            } catch {
              setPushState('prompt');
            }
          }}
          title={pushState === 'denied' ? '系统通知被拒绝，请到 iPad 设置里允许' : '打开孩子申请和任务提醒'}
          aria-label="打开孩子申请和任务提醒"
        >
          <span aria-hidden>{pushState === 'busy' ? '…' : '🔔'}</span>
          <b>{pushState === 'denied' ? '提醒被关' : '打开提醒'}</b>
        </button>
      )}

      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/children" element={<ChildrenPage />} />
        <Route path="/chores" element={<ChoresPage />} />
        <Route path="/challenges" element={<ChallengesPage />} />
        <Route path="/ai" element={<DirectivesPage />} />
        <Route path="/chat-archive" element={<ChatArchivePage />} />
        <Route path="/more" element={<MorePage parent={parent} onLogout={() => setParent(null)} />} />
        <Route path="/settings" element={<SettingsPage parent={parent} onLogout={() => setParent(null)} />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
